import { useCallback } from 'react';
import { useNotificationContext } from '../contexts/NotificationContext';

export const useErrorHandler = () => {
  const { showError, showConnectionError, showAuthError, showLimitReached } = useNotificationContext();

  const handleError = useCallback((error, options = {}) => {
    console.error('Error handled:', error);

    // Se è un errore di rete
    if (!error.response) {
      showConnectionError(options);
      return;
    }

    const status = error.response?.status;
    const data = error.response?.data;

    switch (status) {
      case 401:
        // Errore di autenticazione
        showAuthError(options);
        break;
        
      case 403:
        // Errore di autorizzazione o limite raggiunto
        if (data?.limit_reached && data?.resource_type) {
          showLimitReached(data.resource_type, data.current_count, data.max_allowed, options);
        } else {
          showError(data?.message || 'Non hai i permessi per eseguire questa azione.', {
            title: 'Accesso negato',
            ...options
          });
        }
        break;
        
      case 404:
        showError(data?.message || 'Risorsa non trovata.', {
          title: 'Non trovato',
          ...options
        });
        break;
        
      case 409:
        showError(data?.message || 'Conflitto: la risorsa esiste già.', {
          title: 'Conflitto',
          ...options
        });
        break;
        
      case 422:
        // Errore di validazione
        if (data?.errors) {
          const errorMessages = Object.values(data.errors).flat();
          showError(errorMessages.join(', '), {
            title: 'Errore di validazione',
            ...options
          });
        } else {
          showError(data?.message || 'Dati non validi.', {
            title: 'Errore di validazione',
            ...options
          });
        }
        break;
        
      case 429:
        showError('Troppe richieste. Riprova tra qualche minuto.', {
          title: 'Limite richieste',
          ...options
        });
        break;
        
      case 500:
        showError('Errore interno del server. Riprova più tardi.', {
          title: 'Errore server',
          ...options
        });
        break;
        
      default:
        showError(data?.message || error.message || 'Si è verificato un errore imprevisto.', {
          title: 'Errore',
          ...options
        });
    }
  }, [showError, showConnectionError, showAuthError, showLimitReached]);

  const handleApiError = useCallback((error, context = '') => {
    const errorMessage = context ? `${context}: ${error.message}` : error.message;
    handleError(error, { context: errorMessage });
  }, [handleError]);

  const handleValidationError = useCallback((errors, options = {}) => {
    const errorMessages = Array.isArray(errors) ? errors : Object.values(errors).flat();
    showError(errorMessages.join(', '), {
      title: 'Errore di validazione',
      ...options
    });
  }, [showError]);

  const handleNetworkError = useCallback((options = {}) => {
    showConnectionError(options);
  }, [showConnectionError]);

  const handleAuthError = useCallback((options = {}) => {
    showAuthError(options);
  }, [showAuthError]);

  return {
    handleError,
    handleApiError,
    handleValidationError,
    handleNetworkError,
    handleAuthError
  };
};
